// DonationModal — lightweight, dependency-free dialog used by the
// "Contribuir" / "Support" header button.
//
// Payment channels offered (in order of preference):
//   1. Stripe Checkout — global, cards from 200+ countries. Goes
//      through /api/billing/donate so the donation row is persisted in
//      D1 and the webhook can flip its status to 'paid'.
//   2. PIX (Brazil) — instant, 0% fees. Donor copies the PIX key and
//      pays from their own bank app. Not tracked server-side (no
//      callback) but the simplest path for Brazilian donors.
//
// PayPal was removed in May 2026: the personal/paypal.me flow had too
// many edge-cases (charity-only /donate endpoint, IPN errors on the
// legacy _xclick path) and Stripe Checkout covers the same use case
// with better tracking and a saner reconciliation story.
//
// Env vars consumed (build-time, baked into the bundle):
//   REACT_APP_DONATE_PIX  — PIX key (CPF / e-mail / random) shown to
//                           Brazilian donors. Optional — the PIX block
//                           is hidden when blank.
//
// Defaults below match the maintainer's setup so donations work
// out-of-the-box even before Cloudflare env vars are configured.

import { useEffect, useState } from 'react';
import { Heart, X, Copy, Check, CreditCard, Loader2, Server, Sparkles } from 'lucide-react';
import { useI18n } from '../i18n';
import { getBillingConfig, startDonation, billingErrorMessage } from '../lib/stripe';

// PIX_KEY — fallback PIX key. Override at build time via
// REACT_APP_DONATE_PIX (e.g. swap CPF for an e-mail key without
// touching code).
const PIX_KEY = process.env.REACT_APP_DONATE_PIX || 'anton.mb@hotmail.com';

// Suggested tip amounts (BRL). Donors can also type a custom value in
// the right-most input (min R$5 — matches DONATION_MIN_CENTS on the
// Worker; lower values are rejected server-side).
const PRESETS = [5, 15, 30, 50];

export default function DonationModal({ open, onClose }) {
  const { t, lang } = useI18n();
  const [amount, setAmount] = useState(15);
  const [copied, setCopied] = useState(false);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [stripeErr, setStripeErr] = useState('');
  const [billingCfg, setBillingCfg] = useState(null);
  // Identification fields — both optional. `anonymous=true` overrides
  // name/message on both the client (we send nulls) AND the server
  // (donations.anonymous=1 is honoured by /donations/recent), so the
  // donor can be sure their name will not appear publicly.
  const [donorName, setDonorName] = useState('');
  const [donorMessage, setDonorMessage] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [showIdentity, setShowIdentity] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while the modal is open so the page behind
    // doesn't jiggle on iOS Safari.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Lazy-load the Stripe billing config so we know whether to show
    // the native "Donate with card" button. Fail-closed so the modal
    // never blocks waiting on a slow /billing/config call.
    getBillingConfig().then(setBillingCfg).catch(() => setBillingCfg(null));
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  const stripeEnabled = !!billingCfg?.donation_enabled;

  const donateWithStripe = async () => {
    setStripeErr(''); setStripeBusy(true);
    try {
      await startDonation({
        amount_cents: Math.max(500, Math.round(amount * 100)),
        currency: 'brl',
        name:    anonymous ? null : (donorName.trim()    || null),
        message: anonymous ? null : (donorMessage.trim() || null),
        anonymous,
      });
      // Browser is being redirected; if we still get here, re-enable.
    } catch (e) {
      setStripeErr(billingErrorMessage(e, t));
      setStripeBusy(false);
    }
  };

  if (!open) return null;

  const copyPix = async () => {
    if (!PIX_KEY) return;
    try {
      await navigator.clipboard.writeText(PIX_KEY);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  const titleByLang = {
    pt: 'Ajude a manter o MakerCutt gratuito',
    en: 'Help keep MakerCutt free',
    es: 'Ayuda a mantener MakerCutt gratis',
  };
  // ─── Motivational copy ────────────────────────────────────────────
  // Three short paragraphs — the goal is to make the donor feel that
  // their money goes somewhere concrete (server bills) AND enables
  // something they care about (more free tools). We keep it under
  // ~250 chars per language so it fits without scrolling on mobile.
  const messageByLang = {
    pt: 'O MakerCutt é 100% gratuito, sem anúncios e sem rastreadores. Sua doação cobre servidores, bibliotecas de design e nos permite continuar investindo em novas ferramentas livres para makers, escolas e profissionais. Cada real ajuda. Obrigado! 💛',
    en: 'MakerCutt is 100% free — no ads, no trackers. Your donation pays for servers, design libraries, and lets us keep building new free tools for makers, schools and professionals. Every dollar counts. Thank you! 💛',
    es: 'MakerCutt es 100% gratuito, sin anuncios ni rastreadores. Tu donación cubre servidores, bibliotecas de diseño y nos permite seguir creando nuevas herramientas gratuitas para makers, escuelas y profesionales. ¡Cada euro ayuda. Gracias! 💛',
  };
  // Two micro-bullets right above the amount selector. Reinforces the
  // value proposition with concrete (not abstract) categories.
  const bulletsByLang = {
    pt: [
      { icon: Server,    text: 'Servidores, domínio e armazenamento dos seus projetos.' },
      { icon: Sparkles,  text: 'Mais ferramentas gratuitas: gravação, jigs, novos materiais.' },
    ],
    en: [
      { icon: Server,    text: 'Servers, domain and storage for your projects.' },
      { icon: Sparkles,  text: 'More free tools: engraving, jigs, new materials.' },
    ],
    es: [
      { icon: Server,    text: 'Servidores, dominio y almacenamiento de tus proyectos.' },
      { icon: Sparkles,  text: 'Más herramientas gratuitas: grabado, plantillas, materiales.' },
    ],
  };
  const labels = {
    chooseAmount: { pt: 'Escolha um valor', en: 'Choose an amount', es: 'Elige un valor' },
    other:        { pt: 'Outro',           en: 'Other',           es: 'Otro' },
    payWithStripe:{ pt: 'Doar com cartão', en: 'Donate with card', es: 'Donar con tarjeta' },
    stripeSecure: { pt: 'Pagamento seguro via Stripe · cartão de crédito, débito e Apple/Google Pay',
                    en: 'Secure payment via Stripe · credit, debit, Apple/Google Pay',
                    es: 'Pago seguro vía Stripe · crédito, débito, Apple/Google Pay' },
    pixTitle:     { pt: 'PIX (Brasil) — instantâneo, sem taxa',
                    en: 'PIX (Brazil) — instant, no fees',
                    es: 'PIX (Brasil) — instantáneo, sin tasa' },
    copyPix:      { pt: 'Copiar chave PIX', en: 'Copy PIX key', es: 'Copiar clave PIX' },
    pixCopied:    { pt: 'Copiado!',        en: 'Copied!',        es: '¡Copiado!' },
    pixHint:      { pt: 'Cole no app do seu banco e informe o valor escolhido acima.',
                    en: 'Paste this into your bank app and enter the amount selected above.',
                    es: 'Pega esto en la app de tu banco e informa el valor seleccionado arriba.' },
    cancel:       { pt: 'Fechar',          en: 'Close',          es: 'Cerrar' },
    or:           { pt: 'ou',              en: 'or',             es: 'o' },
    // ─── Identity (Stripe path only) ──────────────────────────────────
    // Identification is OPTIONAL. The "Identificar-se" toggle reveals
    // the name/message inputs; ticking "Doar anonimamente" hides them
    // and sends anonymous=true so the row is never shown publicly even
    // if name/message survived a previous typing session.
    identify:     { pt: 'Quer se identificar? (opcional)',
                    en: 'Want to identify yourself? (optional)',
                    es: '¿Quieres identificarte? (opcional)' },
    identifyHint: { pt: 'Apareça na futura lista de apoiadores.',
                    en: 'Show up on the future supporters wall.',
                    es: 'Aparece en el muro de apoyadores.' },
    nameLabel:    { pt: 'Nome (público)',      en: 'Name (public)',      es: 'Nombre (público)' },
    namePh:       { pt: 'Como devo te chamar', en: 'How should I call you', es: 'Cómo te llamo' },
    messageLabel: { pt: 'Mensagem (opcional)', en: 'Message (optional)', es: 'Mensaje (opcional)' },
    messagePh:    { pt: 'Um recadinho rápido (até 200 caracteres)',
                    en: 'A quick note (up to 200 characters)',
                    es: 'Un mensaje rápido (hasta 200 caracteres)' },
    anonLabel:    { pt: 'Doar anonimamente',   en: 'Donate anonymously', es: 'Donar de forma anónima' },
  };
  const L = (k) => (labels[k][lang] || labels[k].pt);
  const bullets = bulletsByLang[lang] || bulletsByLang.pt;

  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-black/55 px-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-zinc-200 shadow-2xl w-full max-w-md overflow-hidden max-h-[92vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        {/* Header strip with heart and close */}
        <div className="bg-gradient-to-r from-fuchsia-500 via-rose-500 to-amber-500 text-white px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5" fill="currentColor" />
            <span className="font-chunky uppercase tracking-[0.2em] text-xs">{t('support') || 'Apoiar'}</span>
          </div>
          <button onClick={onClose} aria-label="close"
                  className="text-white/80 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          <h3 className="font-chunky text-xl text-zinc-950 leading-tight mb-2">
            {titleByLang[lang] || titleByLang.pt}
          </h3>
          <p className="text-sm text-zinc-600 leading-relaxed mb-4">
            {messageByLang[lang] || messageByLang.pt}
          </p>

          {/* Micro-bullets — what the donation buys, concretely. */}
          <ul className="mb-5 space-y-1.5">
            {bullets.map((b, i) => {
              const Ico = b.icon;
              return (
                <li key={i} className="flex items-start gap-2 text-xs text-zinc-700">
                  <Ico className="w-3.5 h-3.5 mt-0.5 text-rose-500 shrink-0" />
                  <span>{b.text}</span>
                </li>
              );
            })}
          </ul>

          {/* Amount selector */}
          <div className="text-[10px] font-chunky uppercase tracking-[0.22em] text-zinc-500 mb-2">
            {L('chooseAmount')}
          </div>
          <div className="grid grid-cols-5 gap-2 mb-5">
            {PRESETS.map((v) => (
              <button key={v}
                      onClick={() => setAmount(v)}
                      className={`py-2 rounded-lg font-chunky text-sm border transition-colors ${
                        amount === v
                          ? 'bg-zinc-950 text-cyan-300 border-zinc-950'
                          : 'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400'
                      }`}>
                R${v}
              </button>
            ))}
            <input type="number" min="1" step="1" value={amount}
                   onChange={(e) => setAmount(Math.max(1, Number(e.target.value) || 1))}
                   className="py-2 px-2 rounded-lg border border-zinc-200 text-center font-mono text-sm"
                   aria-label={L('other')} />
          </div>

          {/* ─── Identity block (applies to Stripe checkout path) ───────
              The PIX flow doesn't go through our backend, so these
              fields are simply ignored if the donor uses that path.
              They surface name + public message into the `donations`
              row created by /api/billing/donate, which the future
              "wall of supporters" feed will read. */}
          {stripeEnabled && (
            <div className="mb-4">
              {!showIdentity ? (
                <button onClick={() => setShowIdentity(true)}
                        className="text-xs text-zinc-600 hover:text-zinc-900 underline-offset-2 hover:underline">
                  {L('identify')}
                </button>
              ) : (
                <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-3 space-y-2">
                  <div className="text-[10px] font-mono text-zinc-500 -mb-1">
                    {L('identifyHint')}
                  </div>
                  <label className="block">
                    <span className="text-[10px] font-chunky uppercase tracking-[0.22em] text-zinc-500">
                      {L('nameLabel')}
                    </span>
                    <input type="text" maxLength={60}
                           value={donorName}
                           onChange={(e) => setDonorName(e.target.value)}
                           placeholder={L('namePh')}
                           disabled={anonymous}
                           className="mt-1 w-full px-3 py-1.5 rounded border border-zinc-300 text-sm disabled:bg-zinc-100 disabled:text-zinc-400" />
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-chunky uppercase tracking-[0.22em] text-zinc-500">
                      {L('messageLabel')}
                    </span>
                    <textarea rows={2} maxLength={200}
                              value={donorMessage}
                              onChange={(e) => setDonorMessage(e.target.value)}
                              placeholder={L('messagePh')}
                              disabled={anonymous}
                              className="mt-1 w-full px-3 py-1.5 rounded border border-zinc-300 text-sm resize-none disabled:bg-zinc-100 disabled:text-zinc-400" />
                    <div className="mt-0.5 text-[10px] font-mono text-zinc-400 text-right">
                      {donorMessage.length}/200
                    </div>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input type="checkbox"
                           checked={anonymous}
                           onChange={(e) => setAnonymous(e.target.checked)}
                           className="rounded border-zinc-400" />
                    <span className="text-xs text-zinc-700">{L('anonLabel')}</span>
                  </label>
                </div>
              )}
            </div>
          )}

          {/* ─── Primary CTA: Stripe Checkout ─────────────────────────
              The brand uses a gradient identical to the header strip
              so the donor's eye is naturally pulled here. Falls back
              to a disabled state with an explanation when the billing
              API isn't reachable (rare — only happens during a Worker
              outage). */}
          {stripeEnabled ? (
            <button onClick={donateWithStripe} disabled={stripeBusy}
                    className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-fuchsia-500 via-rose-500 to-amber-500 hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed text-white font-chunky uppercase tracking-wider py-3 rounded-lg transition-all shadow-md">
              {stripeBusy
                ? <><Loader2 className="w-4 h-4 animate-spin" /> {lang === 'en' ? 'Redirecting…' : 'Redirecionando…'}</>
                : <><CreditCard className="w-4 h-4" /> {L('payWithStripe')} — R${amount}</>}
            </button>
          ) : (
            <button disabled
                    className="w-full inline-flex items-center justify-center gap-2 bg-zinc-300 text-zinc-500 font-chunky uppercase tracking-wider py-3 rounded-lg cursor-not-allowed">
              <CreditCard className="w-4 h-4" /> {L('payWithStripe')}
            </button>
          )}
          <div className="text-[10px] font-mono text-zinc-500 text-center mt-1.5">
            {L('stripeSecure')}
          </div>
          {stripeErr && (
            <div className="mt-2 text-xs text-rose-600">{stripeErr}</div>
          )}

          {/* Subtle "or" divider between the two payment channels. */}
          {PIX_KEY && (
            <div className="my-5 flex items-center gap-3" aria-hidden>
              <div className="flex-1 h-px bg-zinc-200" />
              <span className="text-[10px] font-chunky uppercase tracking-[0.22em] text-zinc-400">
                {L('or')}
              </span>
              <div className="flex-1 h-px bg-zinc-200" />
            </div>
          )}

          {/* ─── PIX block (Brazil) ──────────────────────────────────
              Instant, 0% fees, no backend round-trip. The donor
              chooses the amount in their own bank app — we just hand
              over the key. Hidden when no PIX_KEY is configured. */}
          {PIX_KEY && (
            <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50/60">
              <div className="text-[10px] font-chunky uppercase tracking-[0.22em] text-emerald-700 mb-2">
                {L('pixTitle')}
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs font-mono text-zinc-800 truncate">{PIX_KEY}</code>
                <button onClick={copyPix}
                        className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white font-chunky uppercase tracking-wider flex items-center gap-1 shrink-0">
                  {copied
                    ? <><Check className="w-3.5 h-3.5" /> {L('pixCopied')}</>
                    : <><Copy  className="w-3.5 h-3.5" /> {L('copyPix')}</>}
                </button>
              </div>
              <p className="mt-2 text-[11px] text-emerald-900/70 leading-snug">{L('pixHint')}</p>
            </div>
          )}

          <button onClick={onClose}
                  className="mt-5 w-full text-xs text-zinc-500 hover:text-zinc-800 py-1">
            {L('cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
